import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ReferenceLine, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import "./Kpp.css";

export default function Kpp() {
  const [input, setInput] = useState<string>('');
  const [maxPoint, setMaxPoint] = useState<number>(7000);
  const [maxShopNum, setMaxShopNum] = useState<number>(10);

  useEffect(() => {
    document.title = '完走師';
    (document.querySelector("link[rel*='icon']") as HTMLLinkElement).href = '/kansousi/logo/favicon.ico';

    const savedInput = localStorage.getItem('kansousiInput');
    if (savedInput) {
      setInput(savedInput);
    }
  }, []);

  function setInputWrapper(input: string) {
    setInput(input);
    localStorage.setItem('kansousiInput', input);
  }


  let currShopNum = 0;
  let currRatePer = 0;
  let currPoint = 0;
  let restPoint = maxPoint;
  let currPayYen = 0;
  let thisItemPoint = 0;
  let otherItemPoint = 0;

  let msg = '';

  /*
表示内容

【エントリー前】
キャンペーンポイント内訳
ショップ買いまわり未エントリー
キャンペーン期間中にエントリーすれば、エントリ

【0店】
キャンペーンポイント内訳
送料無料ライン対応ショップ限定キャンペーン未エントリー

【1店】
キャンペーンポイント内訳
ショップ買いまわり 2ショップ +1倍
この商品購入分6,988 pt※1
対象期間内の購入分12 pt
この商品のご購入で、期間内の対象サービスのこれまでのご購入分も+1倍になりま

【6店】
キャンペーンポイント内訳
ショップ買いまわり 7ショップ +6倍
この商品購入分484 pt※1
対象期間内の購入分1,086 pt

【9店】
ショップ買いまわり 10ショップ +9倍
この商品購入分5,047 pt※1
対象期間内の購入分217 pt

【10店】特殊な形式
ショップ買いまわり 10ショップ +9倍4,687 pt※1

【6店 & ㌽完走後】
キャンペーンポイント内訳
ショップ買いまわり 7ショップ +6倍上限達成済み※2
買いまわり（ラクマ特典） +1倍上限達成済み※2

【7店 & ㌽完走後】
ショップ買いまわり 8ショップ +7倍上限達成済み※2

【9店 & ㌽完走後】
ショップ買いまわり 10ショップ +9倍上限達成済み※2

【11店 & ㌽完走後】
ショップ買いまわり 10ショップ +9倍上限達成済み※2

*/

  const match1 = input.match(/り (\d+)ショップ \+(\d+)倍/);
  if (match1) {
    currShopNum = Number(match1[1]) - 1;
    currRatePer = currShopNum - 1;

    const match2 = input.replace(/,/g, '').match(/この商品購入分(\d+) pt/);
    if (match2) {
      thisItemPoint = Number(match2[1]);
    } else {
      thisItemPoint = 0;
    }

    const match3 = input.replace(/,/g, '').match(/対象期間内の購入分(\d+) pt/);
    if (match3) {
      otherItemPoint = Number(match3[1]);
    } else {
      otherItemPoint = 0;
    }

    // 店舗数完走した特殊ケース
    // 入力例： ショップ買いまわり 10ショップ +9倍4,687 pt※1
    const match4 = input.replace(/,/g, '').match(/り \d+ショップ \+\d+倍\s*(\d+)\s*pt/);
    if (match4) {
      otherItemPoint = Number(match4[1]);
      thisItemPoint = 0;
      currShopNum = maxShopNum;
      currRatePer = currShopNum - 1;
    }

    restPoint = thisItemPoint + otherItemPoint;
    currPoint = maxPoint - restPoint;
    if (currPoint === 0) {
      currPayYen = 0;
    } else {
      currPayYen = Math.ceil(currPoint / (currRatePer / 100) / 100) * 100; // 100円単位で切り上げ
    }
  } else {
    if (input.match(/ショップ買いまわり未エントリー/)) {
      msg += 'とりま、お買い物マラソンにエントリーして ';
    }
    currShopNum = 0;
    currRatePer = 0;
    currPoint = 0;
    restPoint = maxPoint;
    currPayYen = 0;
  }

  let currPointRatePer = Math.ceil(currPoint / maxPoint * 100);
  currPointRatePer = 100 < currPointRatePer ? 100 : currPointRatePer;

  const restPointRatePer = 100 - currPointRatePer;

  // 進捗バーまわりの画像変化
  const runnerImgHeightPx = 92;
  let runnerImgWidthPx = 62;
  let runnerImg = '/kppResource/run.png';
  if (currPointRatePer === 0) {
    runnerImg = '/kppResource/start.png';
  } else if (currPointRatePer === 100) {
    runnerImg = '/kppResource/end.png';
  }
  const scale = currPointRatePer / 100;
  runnerImgWidthPx = runnerImgWidthPx * 0.4 + runnerImgWidthPx * 0.6 * scale;
  const runnerImgOpacity = 0.6 + 0.4 * scale;

  if (currShopNum === 1) {
    const yen = otherItemPoint * 100;
    msg += `約${roundLocal(yen)}円(税抜)を購入済みだが、1店だけなので0㌽ `;
  }

  // ㌽完走済みで、買いまわり店舗数が上限に近く店舗数や㌽還元率が不透明な状態
  // ちなみに、㌽完走していなければ切り分けが可能なため不透明な状態にならない
  const isOpaque = restPoint === 0 && maxShopNum <= currShopNum + 1;

  const shopNums = ((start, end) => Array.from({ length: end - start + 1 }, (_, index) => start + index))
    (Math.max(currShopNum, 2), maxShopNum);

  const chartData = shopNums.map((shopNum) => {
    const ratePer = shopNum - 1;
    const label = `${shopNum}店 (${ratePer}%)`;
    const maxPayYen = Math.ceil(maxPoint / (ratePer / 100) / 100) * 100; // 100円単位に切り上げる
    let restPayYen = maxPayYen - currPayYen;
    restPayYen = restPayYen < 0 ? 0 : restPayYen;
    const reachedPayYen = Math.min(currPayYen, maxPayYen);
    const currShop = (shopNum === currShopNum);

    //次のtooltipはRechartsとしては不要。アプリで使う。
    const tooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
      return (
        <>
          {active && payload && payload.length ? (
            <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>

              <div style={{ textAlign: 'center' }}>買いまわり{label}</div>
              <table>
                <tbody>
                  <tr>
                    <th></th>
                    <th>獲得済分</th>
                    <th>完走まで</th>
                    <th>完走額</th>
                  </tr>
                  <tr>
                    <th>税抜</th>
                    <td>{roundLocal(reachedPayYen)}円</td>
                    <td>{roundLocal(restPayYen)}円</td>
                    <td>{roundLocal(maxPayYen)}円</td>
                  </tr>
                  <tr>
                    <th>8%税込</th>
                    <td>{roundLocal(tax8(reachedPayYen))}円</td>
                    <td>{roundLocal(tax8(restPayYen))}円</td>
                    <td>{roundLocal(tax8(maxPayYen))}円</td>
                  </tr>
                  <tr>
                    <th>10%税込</th>
                    <td>{roundLocal(tax10(reachedPayYen))}円</td>
                    <td>{roundLocal(tax10(restPayYen))}円</td>
                    <td>{roundLocal(tax10(maxPayYen))}円</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      );
    }

    const retObj: any = { label, shopNum, reachedPayYen: round(reachedPayYen), restPayYen: round(restPayYen), ratePer, maxPayYen, currPayYen, currShop, tooltip }

    // 0円はグラフに表示しない
    if (reachedPayYen === 0) {
      delete retObj.reachedPayYen;
    }
    if (restPayYen === 0) {
      delete retObj.restPayYen;
    }

    return retObj;
  });

  return (
    <div className='kpp' style={{ margin: '0 0.3rem 0.6rem 0.3rem' }}>
      <div style={{ fontSize: 'large' }} >
        <span style={{ fontSize: 'larger', fontWeight: 'bold', padding: '0.1rem 0.4rem 0.1rem 0.4rem', backgroundColor: 'purple', color: 'white' }}>完走師</span>
        <span style={{ color: 'dimgray', marginLeft: '0.5rem' }}>～お買い物マラソンを助太刀～</span>
      </div>

      <div>
        {usageDialog()}
        使い方を
        <button style={{ padding: '0.3rem' }}
          onClick={(event) => {
            event.preventDefault();
            const dialog = document.querySelector('dialog') as HTMLDialogElement;
            dialog.showModal();
          }}>動画で見る</button>
      </div>


      <div>
        < h2>1. コピー</h2>
        <div>

          <BrowserView>
            下の商品ページから内訳をコピー

            <iframe
              src="https://item.rakuten.co.jp/nekodan/original-cat-flowers/#offers"
              style={{
                width: '92%', height: '500px', display: 'block', margin: '0 auto',
                border: '10px solid orange', borderRadius: '10px'
              }}
              title="楽天商品ページ"
              loading="lazy"          >
            </iframe>
          </BrowserView>
          <MobileView>

            <a href="https://item.rakuten.co.jp/nekodan/original-cat-flowers/#offers" target="_blank" rel="noopener noreferrer">「猫絵」</a>
            にアクセスして内訳をコピー

            <details open={false} style={{ marginTop: '1em' }}>
              <summary style={{ textDecoration: 'underline solid' }}>トラブル：テキスト範囲選択できない</summary>
              <div style={{ border: '1px solid gray' }}>
                楽天市場アプリで開くとテキスト範囲選択できずコピーできない<br />
                【Web版を開く方法】<br />
                商品ページへのリンクを長押ししてメニューから次を選ぶ<br />
                ・Safari は「バックグラウンドで開く」<br />
                ・Chrome は「新しいタブで開く」<br />
              </div>
            </details>
          </MobileView>

          <p style={{ marginTop: '1rem' }}>
            ※ 楽天市場にログインした状態でアクセス
          </p>
          <p style={{ marginTop: '0.5em' }}>
            ※ <a href="https://item.rakuten.co.jp/nekodan/original-cat-flowers/#offers" target="_blank" rel="noopener noreferrer">「猫絵」</a> の代替は
            <a href="https://item.rakuten.co.jp/inaba-jsh/sgn-358mub-5-t/#offers" target="_blank" rel="noopener noreferrer">「物置」</a>や
            <a href="https://item.rakuten.co.jp/selection-j/mlb-220518oht03/#offers" target="_blank" rel="noopener noreferrer">「サイン」</a> など 高い商品ならOK
          </p>

        </div>
      </div>

      <div>
        <h2>2. 貼り付け</h2>
        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
          <textarea value={input} onChange={(e) => setInputWrapper(e.target.value)} placeholder="ココに貼り付け(ペースト)"
            style={{ width: "70%", height: "10rem" }} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button style={{ margin: '1rem', padding: '0.5rem' }}
              onClick={() => setInputWrapper('')}>
              クリア
            </button>
          </div>
        </div>
      </div>

      <div>
        <h2>3. 見る</h2>

        <table style={{ margin: '0 auto 0 auto' }}>
          <tbody>
            <tr>
              <th>現在の買いまわり</th>
              <td>{currShopNum}店 {isOpaque ? '以上' : ''}</td>
            </tr>
            <tr>
              <th>付与率</th>
              <td>{currRatePer}% {isOpaque ? '以上' : ''}</td>
            </tr>
          </tbody>
        </table>
        {msg && (<div style={{ textAlign: 'center', fontSize: 'larger', color: 'red' }}>※{msg}</div>)}

        <div style={{ marginTop: '2.5rem' }}>
          <h3>ポイント視点</h3>

          <div style={{ margin: '0 auto 0 auto', width: '94%' }}>
            <div style={{
              height: `${runnerImgHeightPx}px`,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <img style={{
                width: `${runnerImgWidthPx}px`,
                position: 'absolute',
                bottom: 0,
                left: `calc(${currPointRatePer} * (100% - ${runnerImgWidthPx}px) / 100)`,
                opacity: runnerImgOpacity,
              }} src={runnerImg} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>始</span>
              <input type="range" min="0" value={currPoint} max={maxPoint} onChange={() => { }} list="rangeLabelList"
                style={{ width: '100%' }} />
              <span>完</span>
              <datalist id="rangeLabelList">
                <option value="0"></option>
                <option value={currPoint}></option>
                <option value={maxPoint}></option>
              </datalist>
            </div>
          </div>

          <div style={{ margin: '0 auto 0 auto', width: '94%', display: 'flex', justifyContent: 'space-between' }}>
            <span>0㌽</span>
            <span>獲得済 <span className='important'>{currPoint.toLocaleString()}</span>㌽</span>
            <span>残 <span className='important'>{restPoint.toLocaleString()}</span>㌽</span>
            <span>{maxPoint.toLocaleString()}㌽</span>
          </div>

          <div style={{ margin: '0 auto 0 auto', width: '94%', display: 'flex', justifyContent: 'space-between' }}>
            <span></span>
            <span>( <span className='important'>{currPointRatePer}</span>% )</span>
            <span>( <span className='important'>{restPointRatePer}</span>% )</span>
            <span></span>
          </div>

        </div>

        {isOpaque || (
          <div style={{ marginTop: '2.5rem', width: '100%' }}>
            <h3>購入額視点</h3>

            <div style={{ margin: '0 auto 0 auto', width: '94%', height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  width={100}
                  height={100}
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 0, right: 2, bottom: 20, left: 2 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" label={{ value: '購入額', position: 'bottom' }} tickFormatter={n => n.toLocaleString() + '円'} />
                  <YAxis dataKey="label" label={{ value: '買いまわりショップ数', position: 'insideLeft', angle: -90 }} type="category" />
                  <Tooltip /* <bar>より下にするとマウスオーバー時に表示がおかしくなる。 */
                    content={({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
                      return chartData.find(a => a.label === label)?.tooltip({ active, payload, label });
                    }}
                    cursor={{ stroke: 'pink', strokeWidth: 1 }}
                  />
                  <Bar dataKey="reachedPayYen" stackId="a" fill="#87ceeb">
                    <LabelList dataKey="reachedPayYen" position="center" formatter={(n: number) => n.toLocaleString() + '円'} fill="#000000" />
                  </Bar>
                  <Bar dataKey="restPayYen" stackId="a" fill="hotpink" label={ //合計値ラベル
                    { position: 'right', formatter: (n: number) => n.toLocaleString() + '円', fill: "#000000" }
                  } >
                    <LabelList dataKey="restPayYen" position="center" formatter={(n: number) => n.toLocaleString() + '円'} fill="#000000" />
                  </Bar>
                  <Legend verticalAlign="top"
                    payload={[
                      { value: '㌽獲得済み購入額(税抜)', type: 'square', color: '#87ceeb' },
                      { value: '完走に必要な購入額(税抜)', type: 'square', color: 'hotpink' },
                    ]}
                  />
                  <ReferenceLine x={currPayYen} stroke='brown' />

                  {(2 <= currShopNum) &&
                    <ReferenceLine
                      y={chartData[0]?.label}
                      stroke="orange"
                      opacity={0.25}
                      strokeWidth={25} strokeLinecap="round" strokeDasharray="0 50" // 丸点線
                    />
                  }

                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <details open={false} style={{ marginTop: '5em' }}>
          <summary style={{ color: 'lightgray' }}>カスタマイズ</summary>
          <p>
            買いまわり店上限
            <input type="number" value={maxShopNum} onChange={(e) => setMaxShopNum(Number(e.target.value))} placeholder="最大ショップ数" />
          </p>
          <p>
            付与上限ポイント数
            <input type="number" value={maxPoint} onChange={(e) => setMaxPoint(Number(e.target.value))} placeholder="最大ポイント" />
          </p>
        </details>
      </div>
    </div >
  );
}

//////////////////////////////////////////////////////////////////////

function tax8(price: number) {
  return price * 1.08;
}
function tax10(price: number) {
  return price * 1.1;
}

function roundLocal(n: number) {
  return round(n).toLocaleString();
}

function round(n: number) {
  return Math.round(n);
}

function usageDialog() {
  let videoUrl = 'https://ikura.tokyo/sharefiles/kansousi_pc.mp4';
  if (isMobile) {
    videoUrl = 'https://ikura.tokyo/sharefiles/kansousi.mp4';
  }
  return (<>
    <dialog onClick={(event) => {
      const dialog = event.currentTarget;
      const dialogPos = dialog.getBoundingClientRect();
      if (event.clientX < dialogPos.left || dialogPos.right < event.clientX ||
        event.clientY < dialogPos.top || dialogPos.bottom < event.clientY) {
        dialog.close();
      }
    }}>
      <div style={{ textAlign: 'right' }}>
        <span
          onClick={(event) => {
            const dialog = event.currentTarget.closest('dialog') as HTMLDialogElement;
            dialog.close();
          }}
          style={{ cursor: 'pointer' }}
        >❌</span>
      </div>

      <video style={{ height: '80vh', width: 'auto' }} muted controls >
        <source src={videoUrl} type="video/mp4" />
        お使いのブラウザは動画タグをサポートしていません。
      </video>

    </dialog>
  </>);
}